import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from 'react-query';
import ActivityLogFilters, { getDefaultFilters } from './ActivityLogFilters';
import {
  Page,
  Header,
  TitleBlock,
  Title,
  Subtitle,
  HeaderActions,
  AiBtn,
  Btn,
  Body,
  MainColumn
} from './style';
import ActivityLogTable from './ActivityLogTable';
import ActivityLogDetailPanel from './ActivityLogDetailPanel';
import AiAnalyticsChat from './AiAnalyticsChat';
import { useAuditLogs } from '../../services/auditLog.service';
import { useSignalRListener } from '../../hooks/useSignalR';
import { httpRequest } from '../../config/api';

const downloadCSV = (items, projectsById = {}, periodsById = {}) => {
  const headers = [
    'Timestamp',
    'Action',
    'Project',
    'Period',
    'Resource Type',
    'Resource Name',
    'Resource ID',
    'Reference',
    'Status',
    'Status Code',
    'User',
    'User Email',
    'IP Address',
    'Duration (ms)',
    'Error'
  ];
  const rows = items.map(i => {
    const ctx = i.metadata?.context ?? {};
    const projectName = ctx.project_id ? (projectsById[ctx.project_id] ?? ctx.project_id) : '';
    const periodObj = ctx.period_id ? periodsById[ctx.period_id] : null;
    const periodLabel = periodObj
      ? `${periodObj.period} ${periodObj.year}`
      : (ctx.period_name ?? ctx.period_id ?? '');
    return [
      i.timestamp,
      i.action,
      projectName,
      periodLabel,
      i.resource_type ?? '',
      i.resource_name ?? '',
      i.resource_id ?? '',
      ctx.deep_link ?? '',
      i.status,
      i.status_code ?? '',
      i.user_name ?? i.user_id ?? '',
      i.user_email ?? '',
      i.ip_address ?? '',
      i.duration_ms ?? '',
      i.error_message ?? ''
    ];
  });

  const csv = [headers, ...rows]
    .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const ActivityLogPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract ?highlight=<id> from URL (set when clicking a notification)
  const highlightId = useMemo(() => {
    return new URLSearchParams(location.search).get('highlight') ?? null;
  }, [location.search]);

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInitialQuestion, setChatInitialQuestion] = useState(null);
  const [chatAuditLogId, setChatAuditLogId] = useState(null);

  const handleOpenAiChat = useCallback((question = null, auditLogId = null) => {
    setChatInitialQuestion(question);
    setChatAuditLogId(auditLogId);
    setChatOpen(true);
  }, []);

  const queryFilters = useMemo(() => ({ ...filters, page, size: 12 }), [filters, page]);

  const { data, isLoading, refetch } = useAuditLogs(queryFilters);

  // ── Project / period name resolution ─────────────────────────────────────
  // Reuse the same query key as ActivityLogFilters so no extra HTTP call is made.
  const { data: workflowsData } = useQuery(
    ['AUDIT_FILTER_PROJECTS'],
    () => httpRequest.get('api/workflows/all'),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const projectsById = useMemo(() => {
    const map = {};
    (Array.isArray(workflowsData) ? workflowsData : []).forEach(w => {
      map[w.id] = w.name;
    });
    return map;
  }, [workflowsData]);

  // Collect unique project IDs visible on the current page
  const visibleProjectIds = useMemo(() => {
    if (!data?.items) return [];
    const ids = new Set();
    data.items.forEach(item => {
      const pid = item.metadata?.context?.project_id;
      if (pid) ids.add(pid);
    });
    return [...ids];
  }, [data]);

  // Fetch full workflow details per unique project (gives PeriodResponseSchema with period+year)
  const workflowDetailQueries = useQueries(
    visibleProjectIds.map(id => ({
      queryKey: ['AUDIT_WORKFLOW_DETAIL', id],
      queryFn: () => httpRequest.get(`api/workflows/${id}`),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }))
  );
  const periodsById = useMemo(() => {
    const map = {};
    workflowDetailQueries.forEach(q => {
      (q.data?.periods ?? []).forEach(p => {
        map[p.id] = p;
      });
    });
    return map;
  }, [workflowDetailQueries]);
  // ─────────────────────────────────────────────────────────────────────────

  // Refresh table when a new AuditNotification arrives via SignalR
  useSignalRListener(
    'AuditNotification',
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const handleApplyFilters = useCallback(newFilters => {
    setFilters(newFilters);
    setPage(1);
    setSelectedEntry(null);
  }, []);

  const handleRowClick = useCallback(
    entry => {
      setSelectedEntry(prev => (prev?.id === entry.id ? null : entry));
      // Remove highlight param from URL when user manually clicks a row
      if (highlightId) {
        navigate('/activity-log', { replace: true });
      }
    },
    [highlightId, navigate]
  );

  // Auto-select the highlighted entry after data loads
  useEffect(() => {
    if (highlightId && data?.items) {
      const found = data.items.find(i => i.id === highlightId);
      if (found) setSelectedEntry(found);
    }
  }, [highlightId, data]);

  const totalPages = data?.pages ?? 1;
  const total = data?.total ?? 0;

  return (
    <Page>
      <Header>
        <TitleBlock>
          <Title>Activity log</Title>
          <Subtitle>Audit trail of all actions performed on the platform.</Subtitle>
        </TitleBlock>
        <HeaderActions>
          <AiBtn $active={chatOpen} onClick={() => handleOpenAiChat()} title="AI Analytics">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
              <path d="M19 2 L19.7 4.3 L22 5 L19.7 5.7 L19 8 L18.3 5.7 L16 5 L18.3 4.3 Z" />
              <path d="M5 16 L5.5 17.5 L7 18 L5.5 18.5 L5 20 L4.5 18.5 L3 18 L4.5 17.5 Z" />
            </svg>
            AI Analytics
          </AiBtn>
          <Btn onClick={() => refetch()} title="Refresh">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </Btn>
          {data?.items?.length > 0 && (
            <Btn
              onClick={() => downloadCSV(data.items, projectsById, periodsById)}
              title="Download as CSV"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download as CSV
            </Btn>
          )}
        </HeaderActions>
      </Header>

      <MainColumn style={{ gap: 12, marginBottom: 12 }}>
        <ActivityLogFilters onApply={handleApplyFilters} />
      </MainColumn>

      <Body>
        <MainColumn>
          <ActivityLogTable
            data={data}
            isLoading={isLoading}
            page={page}
            totalPages={totalPages}
            total={total}
            size={12}
            onPageChange={setPage}
            onRowClick={handleRowClick}
            selectedId={selectedEntry?.id}
            highlightId={highlightId}
            projectsById={projectsById}
            periodsById={periodsById}
          />
        </MainColumn>

        {selectedEntry && (
          <ActivityLogDetailPanel
            entry={selectedEntry}
            onClose={() => setSelectedEntry(null)}
            projectsById={projectsById}
            periodsById={periodsById}
            onOpenAiChat={handleOpenAiChat}
          />
        )}

        {chatOpen && (
          <AiAnalyticsChat
            filters={filters}
            onClose={() => {
              setChatOpen(false);
              setChatInitialQuestion(null);
              setChatAuditLogId(null);
            }}
            initialQuestion={chatInitialQuestion}
            auditLogId={chatAuditLogId}
          />
        )}
      </Body>
    </Page>
  );
};

export default ActivityLogPage;
